// START IMPORTS
// Context imports
import { ReportContextProvider } from '@ltvco/refresh-lib/ctx';
// Theme and UI imports
import { Grid, Box } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  DateUtil,
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  LocationOfProperty,
  LotAndBuildingDetails,
  useReport,
  AssessedValue,
  BuildingPermits,
  HomeOwnerAssociationSection,
  DeedsSection,
  LiensSection,
  EstimatedValue,
  ReportFactory,
  DesktopNav,
  NotesSection,
  NaturalHazards,
  ReportLoading,
  PossibleOwners,
  PossibleResidents,
  ReportNavMobile,
  OwnershipTimeline,
  PropertyResults,
  CountyAssessorRecords,
  EquityAndLoans,
  MarketInsights,
  buildForSaleTab,
  buildPreForeclosureTab,
  type peopleTypes,
  Demographics,
  Schools,
  useReportMonitors,
  useTuxedo,
  useSession,
  EditPropertyDetailsModal,
  useRemouladeReportSnapshot,
  ReportChangesOverview,
  DebugMenu,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  ReportActionsWithDateUpdated,
  ReportNullState,
  PdfSection,
  PropertyReportOverview,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// App-specific imports
import { getPropertyNavLinkData } from 'navLinkData/propertyNavLinkData';
import {
  groupAvmsBySource,
  getPropertyDetailsCount,
  getPropertyValuesCount,
} from 'utils/propertyReportUtils';
import { useQueryParams } from 'utils/useQueryParams';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PropertyReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

export function PropertyReportPage({
  permalink,
  isMonitored = false,
}: PropertyReportProps) {
  // START HOOKS_AND_QUERIES
  const queryParams = useQueryParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { reportIsMonitored } = useReportMonitors();
  const {
    userProfile,
    homeDataSet,
    homeAdditions,
    setupUserProfile,
    createUserProfile,
  } = useTuxedo();
  const { session } = useSession();

  // TODO: all this monitored report logic needs a refactor.
  // There is no point in refetching a report when a user toggles monitoring, so we use the initial value.
  const [initialIsMonitored, setInitialIsMonitored] = useState(isMonitored);
  const coreResult = useReport(permalink, initialIsMonitored);
  const remouladeResult = useRemouladeReportSnapshot(
    permalink,
    initialIsMonitored
  );
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [nullPermalink, setNullPermalink] = useState<string | null>(null);
  const [hasTriggeredMutation, setHasTriggeredMutation] = useState(false);
  const [userProfileTuxedo, setUserProfileTuxedo] = useState(null);
  const [readyToFetch, setReadyToFetch] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const queryReportResult = (
    initialIsMonitored ? remouladeResult : coreResult
  ) as typeof coreResult;

  const zeroedReport = isZeroed(queryReportResult);
  useScrollToSectionOnNavigate(hash, queryReportResult);

  const fullAddress = generateFullAddressFromParams(queryParams);
  const firstProperty = queryReportResult?.data?.entities?.properties?.[0];

  const postHomeData = homeDataSet;
  const tuxedoBuilding = homeAdditions(nullPermalink) || { data: null };
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  // If remoulade fails, we fall back to core. Most likely cause is race condition between creating monitor
  // and remoulade fetching data from core.
  useEffect(() => {
    if (remouladeResult.isError) {
      setInitialIsMonitored(false);
    }
  }, [remouladeResult.isError]);

  useEffect(() => {
    if (!firstProperty || hasTriggeredMutation) return;

    if (!readyToFetch) {
      setReadyToFetch(true);
    }
  }, [firstProperty, hasTriggeredMutation]);

  // Handling the user profile creation and query success
  useEffect(() => {
    if (readyToFetch) {
      if (userProfile.isError && !hasTriggeredMutation) {
        if (
          !userProfile?.data?.user_profile &&
          session?.account?.account?.user_info &&
          firstProperty?.mailing_address
        ) {
          const userProfileData = setupUserProfile(
            session.account.account.user_info,
            firstProperty.mailing_address
          );

          userProfileData.record_search = {
            address: firstProperty?.parcel_address?.full || '',
            city: firstProperty?.parcel_address?.parsed?.city || '',
            state: firstProperty?.parcel_address?.parsed?.state || '',
            zip_code: firstProperty?.parcel_address?.parsed?.zip5 || '',
          };

          createUserProfile.mutate({
            userProfile: {
              user_profile: userProfileData,
              record_search: userProfileData.record_search,
            },
          });

          setHasTriggeredMutation(true);
        }
      }

      if (userProfile.isSuccess && userProfile.data?.user_profile) {
        setNullPermalink(permalink);
        setUserProfileTuxedo(userProfile.data.user_profile);
      }
    }
  }, [readyToFetch, userProfile, session, firstProperty, createUserProfile]);

  // Update state when userProfile fetch succeeds
  useEffect(() => {
    if (userProfile.isSuccess && userProfile.data?.user_profile) {
      setNullPermalink(permalink);
      setUserProfileTuxedo(userProfile.data.user_profile);
    }
  }, [userProfile, permalink]);

  console.log('Rendering component...');
  // END EFFECTS

  // START EARLY_RETURNS
  if (queryReportResult.isLoading || queryReportResult.isError) {
    return <ReportLoading menuItems={10} />;
  }

  if (zeroedReport) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START EVENT_HANDLERS
  const handleOpenEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };
  // END EVENT_HANDLERS

  // START DATA_PREPARATION

  const report = ReportFactory.create(queryReportResult?.data, 'property');

  if (!report) return <ReportLoading menuItems={10} />;

  const property = report.data.properties[0];
  const responseHasPropertyDataAndNoEntitiesFlagIsNull =
    (report.data?.meta?.counts?.properties ?? 0) > 0 &&
    queryReportResult?.data?.meta?.no_entities === null;
  const noEntitiesFlagIsEitherTrueOrNull =
    queryReportResult?.data?.meta?.no_entities ||
    queryReportResult?.data?.meta?.no_entities === null;

  if (
    !responseHasPropertyDataAndNoEntitiesFlagIsNull &&
    noEntitiesFlagIsEitherTrueOrNull
  ) {
    const options = queryReportResult?.data?.entities?.options;
    const fromParam = queryParams.get('from') || '';
    if (!options) {
      return <ReportLoading menuItems={10} />;
    }
    return (
      <ReportContextProvider permalink={permalink} reportType="property">
        <Grid
          sx={{
            bgcolor: 'background.default',
            flexDirection: 'column',
            paddingBottom: '20px',
            px: 2,
          }}
          container
        >
          <PropertyResults
            searchAddress={report?.data?.address || fullAddress}
            options={options}
            fromDashboard={fromParam}
          />
        </Grid>
      </ReportContextProvider>
    );
  }

  const userProfileForEditModal = userProfileTuxedo
    ? userProfileTuxedo
    : setupUserProfile(
        session.account?.account?.user_info,
        property?.mailing_address
      );
  let updatedBuilding = property?.buildings?.[0];

  if (
    userProfileTuxedo &&
    tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
  ) {
    const totalRoomsValue =
      tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.beds +
      tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.baths;

    const updatedBuildingTuxedo = {
      ...updatedBuilding,
      rooms: {
        ...updatedBuilding?.rooms,
        bed: tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
          ?.custom_attributes?.beds,
        baths: {
          ...updatedBuilding?.rooms?.baths,
          total:
            tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
              ?.custom_attributes?.baths,
        },
        total: totalRoomsValue,
      },
      area: tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.sqft,
    };
    updatedBuilding = updatedBuildingTuxedo;
  }

  const displayAddress = report.data.address || fullAddress;

  const marketValues = property.market;
  const marketSortedBySource = groupAvmsBySource(marketValues);
  const propertyDetailsCount = getPropertyDetailsCount(property);
  const PropertyValuesCount = getPropertyValuesCount(property);
  const propertyNavLinkData = getPropertyNavLinkData(
    property?.owners.length,
    property?.residents.length,
    property?.building_permits ? property?.building_permits.length : 0,
    property?.deeds.length,
    property?.courts.civils.length,
    propertyDetailsCount || 0,
    PropertyValuesCount || 0,
    report?.data?.properties?.[0]?.equities?.length || 0,
    report?.data?.schools?.length || 0
  );

  const listingsNavigateAction = (property: any, type?: string) => {
    const { city, state, zip5 } = property?.parcel_address?.parsed || {
      city: '',
      state: '',
      zip5: '',
    };
    const url = `/search/listing?city=${city}&state=${state}&zip5=${zip5}&search_type=${
      type || ''
    }`;
    navigate(url);
  };

  const marketInsightsTabs = [
    buildForSaleTab({
      property,
      data: report?.data?.listings?.slice(0, 10) || [],
      navigateAction: listingsNavigateAction,
      shouldUseModal: true,
    }),
    buildPreForeclosureTab({
      property,
      data: report?.data?.preforeclosures?.slice(0, 10) || [],
      navigateAction: listingsNavigateAction,
      shouldUseModal: true,
    }),
  ];

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';
  document.title = `${displayAddress} - NumberGuru`;
  // END DATA_PREPARATION

  // START RENDER
  return (
    <>
      <Grid container direction={'row'} columns={12} spacing={7}>
        <Grid
          size={{
            sm: 12,
            md: 4,
            lg: 4,
          }}
          sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' } }}
        >
          <DesktopNav
            navLinkData={propertyNavLinkData}
            reportDataPoint={displayAddress}
            permalink={permalink}
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 8,
            lg: 8,
          }}
          marginTop={{ xs: 0, md: 0.5 }}
          sx={{
            '&.MuiGrid-item': {
              paddingTop: { xs: 3.75, md: 7 },
            },
          }}
        >
          {reportIsMonitored && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={propertyNavLinkData}
            />
          )}
          <>
            <Box
              sx={{
                height: 30,
                backgroundColor: 'background.default',
                position: 'sticky',
                marginBottom: '-10px',
                marginX: -1,
                top: 52,
                zIndex: 10,
                display: {
                  xs: 'none',
                  sm: 'block',
                  md: 'block',
                  lg: 'block',
                },
              }}
            />

            <ReportActionsWithDateUpdated
              reportType={'property'}
              reportTitle="Property"
              dateUpdated={reportUpdateDate}
              reportUpgraded={false}
            />
          </>
          <PropertyReportOverview
            propertyReportData={property}
            serializedPropertyReportData={report.data}
            permalink={permalink}
            updatedBuilding={updatedBuilding}
            marketValues={marketValues}
            allowClaiming={false}
          />
          <PossibleOwners
            owners={property.owners as peopleTypes.Identities}
            propertyAddress={displayAddress}
            permalink={permalink}
          />
          <OwnershipTimeline
            owners={property.owners as peopleTypes.Identities}
            deeds={property.deeds}
            propertyAddress={displayAddress}
            permalink={permalink}
          />
          <PossibleResidents
            owners={property.owners}
            residents={property.residents}
            propertyAddress={displayAddress}
            permalink={permalink}
          />
          <LotAndBuildingDetails
            propertyAddress={displayAddress}
            property={property}
            canEditProperty={true}
            updatedBuilding={updatedBuilding}
            setIsEditModalOpen={handleOpenEditModal}
            permalink={permalink}
          />
          <LocationOfProperty
            propertyAddress={displayAddress}
            property={property}
            permalink={permalink}
          />
          <HomeOwnerAssociationSection
            propertyAddress={displayAddress}
            hoas={property.hoas}
          />
          <AssessedValue
            propertyAddress={displayAddress}
            property={property}
            permalink={permalink}
          />
          <CountyAssessorRecords
            propertyReportData={property}
            permalink={permalink}
          />
          <BuildingPermits
            propertyAddress={displayAddress}
            property={property}
            permalink={permalink}
          />
          <DeedsSection
            propertyAddress={displayAddress}
            deeds={property.deeds}
            permalink={permalink}
          />
          <LiensSection
            propertyAddress={displayAddress}
            liens={property.courts}
            permalink={permalink}
          />
          <EstimatedValue
            property={property}
            propertyAddress={displayAddress}
            marketValues={marketValues}
            rentalValues={property.values?.rental || []}
            avmsPartners={marketSortedBySource}
            permalink={permalink}
          />
          <EquityAndLoans
            propertyAddress={displayAddress}
            property={report.data}
            permalink={permalink}
          />
          <MarketInsights
            propertyAddress={displayAddress}
            tabs={marketInsightsTabs.filter((item) => !!item)}
          />
          <Schools propertyAddress={displayAddress} property={report.data} />
          <Demographics property={property} propertyAddress={displayAddress} />
          {/* <HomesNearby />
          <SurroundingArea /> */}
          <NaturalHazards
            propertyAddress={displayAddress}
            property={property}
          />
          <PdfSection permalink={permalink} />
          <NotesSection permalink={permalink} />
          {/* <Comments /> */}
          <EditPropertyDetailsModal
            open={isEditModalOpen}
            setIsOpen={handleOpenEditModal}
            building={updatedBuilding}
            permalink={permalink}
            userProfile={userProfileForEditModal}
            postHomeData={postHomeData}
            propertyAddress={displayAddress}
          />
        </Grid>
      </Grid>
      <ReportNavMobile navLinkData={propertyNavLinkData} />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </>
  );
  // END RENDER
}

// START HELPER_FUNCTIONS
function generateFullAddressFromParams(queryParams: URLSearchParams): string {
  const address = queryParams.get('address') || '';
  const city = queryParams.get('city') || '';
  const state = queryParams.get('state') || '';
  const zipcode = queryParams.get('zipcode') || '';

  return `${address} ${city}, ${state} ${zipcode}`;
}
// END HELPER_FUNCTIONS

// function HomesNearby() {
//   return <PlaceholderSection title={'Homes Nearby'} />;
// }

// function SurroundingArea() {
//   return <PlaceholderSection title={'Surrounding Area'} />;
// }
